import { Injectable } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { UserExtendedDTO } from '../models/user/user-b2c-flow-dto.model';
import {UserCompaniesResponseDTO, UserCompany} from '../models/user/user-companies-response-dto.model';
import { UserInfoDTO } from '../models/user/user-info-dto.model';
import {BehaviorSubject, Observable, Subject, of, tap} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {UserInfoVM, UserVM} from '../models/user/user-vm.model';
import { UserDocumentType } from '../enums/user-document-type.enum';
import { Modules } from '../enums/modules.enum';
import {CompaniesService} from './companies.service';

@Injectable({
    providedIn: 'root'
})
export class
UserService {
    url = environment.apiConfig.IdentityAPI.endpoint + '/users';

    private userOrganizationId$: BehaviorSubject<number | undefined> = new BehaviorSubject(undefined as number);
    private userCompanies$ = new BehaviorSubject<UserCompany[]>([]);
    private currentUser$ = new BehaviorSubject<UserVM>({} as UserVM);
    private userCompanyName$ = new BehaviorSubject<string>('');

    constructor(
      private http: HttpClient,
      private companiesService: CompaniesService,
    ) { }

  get companyId(): number {
      return this.companiesService.managedCompanyId;
  }

  getUserOrganizationId(): Observable<undefined | number> {
      return this.userOrganizationId$.asObservable();
  }

  // Like getUserOrganizationId() but with a fallback fetch when the BehaviorSubject is empty.
  // Used by pages that may be entered via a direct URL — the access guard returns true off the
  // localStorage 'organization' key without refreshing the subject, so a page that landed
  // straight here sees `undefined` and would otherwise short-circuit before loading anything.
  // Returns once with a resolved value, then completes.
  resolveUserOrganizationId(): Observable<number | null> {
    return this.userOrganizationId$.pipe(
      take(1),
      switchMap(id => {
        if (id != null) return of(id as number);
        return this.getUserCompanies().pipe(
          map(resp => resp.organizationId ?? null),
          catchError(() => of(null))
        );
      })
    );
  }

  getUserCompanyName(): Observable<string> {
      return this.userCompanyName$.asObservable();
  }

    createUserfromADb2c(user: UserExtendedDTO) {
        return this.http.post<void>(this.url, user);
    }

  getUserCompanies() {
    return this.http.get<UserCompaniesResponseDTO>(this.url + '/companies').pipe(
      tap((organization) => {
        const managedCompanyName = organization.companies.find(c => c.id === this.companyId)?.name;
        this.userCompanies$.next(organization.companies);
        this.userCompanyName$.next(managedCompanyName);
        this.userOrganizationId$.next(organization.organizationId);
      })
    )
  }

  getUserCompaniesAsObs(): Observable<UserCompany[]> {
      return this.userCompanies$.asObservable();
  }

    getCurrentUser(includeDetails: boolean = true): Observable<UserVM> {
        return this.http.get<UserVM>(`${this.url}/current?includeDetails=${includeDetails}`).pipe(
          tap((userVM: UserVM) => {
            this.currentUser$.next(userVM);
          })
        );
    }

    getCurrentUserAsObs(): Observable<UserVM> {
      return this.currentUser$.asObservable();
    }

    updateUserInfo(userInfo: UserInfoDTO): Observable<void> {
        return this.http.put<void>(this.url, userInfo);
    }

    uploadDocument(documentType: UserDocumentType, file: File) : Observable<void> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<void>(this.url + `/upload?documentType=${documentType}`, formData);
    }

    getModulePermissions(module: Modules) : Observable<string[]> {
        return this.http.get<string[]>(this.url + `/module/${module}/permissions`);
    }

    getModulePermissionsById(companyId: number, module: number): Observable<string[]> {
      return this.http.get<string[]>(this.url + `/companies/${companyId}/module/${module}/permissions`)
    }



}
